import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import "./BootcampsPage.css"
import { Calendar, Code, BookOpen, Award, Target, Clock, MapPin, ChevronLeft, ChevronRight, CheckCircle, Users } from "lucide-react";
import qiskit from '../../assets/qiskit.jpg';
import classiq from '../../assets/classiq.jpg';
import cirq from '../../assets/cirq.jpg';
import pennylane from '../../assets/pennylane.jpg';
import forest from '../../assets/forest.jpg';
import bracket from '../../assets/bracket.jpg';

const BootcampsPage = () => {
  const [currentCard, setCurrentCard] = useState(0);

  const handlePrev = () => {
    setCurrentCard((prev) => (prev === 0 ? pastBootcamps.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentCard((prev) => (prev === pastBootcamps.length - 1 ? 0 : prev + 1));
  };

  const frameworks = [
    { name: "Qiskit", logo: qiskit },
    { name: "Classiq", logo: classiq },
    { name: "Cirq", logo: cirq },
    { name: "PennyLane", logo: pennylane },
    { name: "Forest", logo: forest },
    { name: "Braket", logo: bracket },
  ];

  const steps = [
    { step: "01", icon: BookOpen, color: "blue",   title: "Learn Fundamentals", desc: "Master quantum computing theory and mathematical foundations" },
    { step: "02", icon: Code,     color: "purple", title: "Hands-on Coding",    desc: "Build quantum circuits and algorithms using industry frameworks" },
    { step: "03", icon: Target,   color: "green",  title: "Real Projects",      desc: "Apply knowledge to solve real-world quantum computing challenges" },
    { step: "04", icon: Award,    color: "orange", title: "Get Certified",      desc: "Earn industry-recognized certificates and join our alumni network" },
  ];

  const pastBootcamps = [
    {
      title: "Quantum Machine Learning Intensive",
      framework: "Qiskit + PennyLane",
      date: "March 15-17, 2024",
      duration: "3 Days",
      level: "Intermediate",
      participants: 45,
      maxParticipants: 60,
      location: "Hybrid (Nairobi + Online)",
      price: "Free",
      description: "Dive into quantum machine learning algorithms and practical implementations.",
      color: "teal",
    },
    {
      title: "Quantum Computing Fundamentals",
      framework: "Classiq",
      date: "April 8-12, 2024",
      duration: "5 Days",
      level: "Beginner",
      participants: 78,
      maxParticipants: 100,
      location: "Online",
      price: "Free",
      description: "Perfect introduction to quantum computing concepts and hands-on programming.",
      color: "purple",
    },
    {
      title: "Advanced Quantum Algorithms Bootcamp",
      framework: "Cirq + Classiq",
      date: "September 20-24, 2024",
      duration: "5 Days",
      level: "Advanced",
      participants: 23,
      maxParticipants: 40,
      location: "iHub Nairobi",
      price: "KES 5,000",
      description: "Explore cutting-edge quantum algorithms and optimization techniques.",
      color: "blue",
    },
  ];


  const BootcampCard = ({ bootcamp }) => (
    <div className={`bootcamps-past-card bootcamps-card-${bootcamp.color}`}>
      <div className="bootcamps-past-card-header">
        <div className="bootcamps-past-card-badges">
          <span className="bootcamps-past-level-badge">{bootcamp.level}</span>
          <span className="bootcamps-past-price-badge">{bootcamp.price}</span>
        </div>
        <h3 className="bootcamps-past-card-title">{bootcamp.title}</h3>
        <p className="bootcamps-past-card-desc">{bootcamp.description}</p>
      </div>
      <div className="bootcamps-past-card-body">
        <div className="bootcamps-past-details">
          <div className="bootcamps-past-detail">
            <Code className="bootcamps-past-icon" /><span>{bootcamp.framework}</span>
          </div>
          <div className="bootcamps-past-detail">
            <Calendar className="bootcamps-past-icon" /><span>{bootcamp.date}</span>
          </div>
          <div className="bootcamps-past-detail">
            <Clock className="bootcamps-past-icon" /><span>{bootcamp.duration}</span>
          </div>
          <div className="bootcamps-past-detail">
            <MapPin className="bootcamps-past-icon" /><span>{bootcamp.location}</span>
          </div>
        </div>
        <div className="bootcamps-past-progress">
          <div className="bootcamps-past-progress-header">
            <div className="bootcamps-past-progress-left">
              <Users className="bootcamps-past-icon" /><span>Participants</span>
            </div>
            <span className="bootcamps-past-progress-count">
              {bootcamp.participants}/{bootcamp.maxParticipants}
            </span>
          </div>
          <div className="bootcamps-progress-bar-bg">
            <div
              className={`bootcamps-progress-bar bootcamps-progress-${bootcamp.color}`}
              style={{ width: `${(bootcamp.participants / bootcamp.maxParticipants) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bootcamps-page">
      <Helmet>
        <title>Quantum Bootcamps | QuantumKE</title>
        <meta name="description" content="Hands-on quantum computing bootcamps covering Qiskit, PennyLane, Cirq, and more. Join QuantumKE's intensive training programs designed for all skill levels across Kenya and Africa." />
        <link rel="canonical" href="https://quantumke.org/bootcamps" />
      </Helmet>
      {/* Page Banner */}
      <section className="bootcamps-banner">
        <div className="bootcamps-banner-overlay"></div>
        <div className="bootcamps-banner-content">
          <h1 className="bootcamps-banner-title">Bootcamp Programs</h1>
          <p className="bootcamps-banner-subtitle">
            Participate in our bootcamps and gain the skills, confidence, and community to thrive in quantum tech.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bootcamps-hero-section">
        <div className="bootcamps-container">
          <div className="bootcamps-hero-content">
            <div className="bootcamps-hero-text">
              <h1 className="bootcamps-hero-title">
                Explore your path to
                <span className="bootcamps-gradient-text"> Quantum </span>
                
              </h1>
              <p className="bootcamps-hero-description">
                Join a growing community of quantum enthusiasts across Africa. Build practical skills through
                structured bootcamps, work alongside experts, and take your first steps into one of the most
                exciting fields in tech.
              </p>
              <div className="bootcamps-hero-stats">
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">200+</div>
                  <div className="bootcamps-stat-label">Graduates</div>
                </div>
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">15+</div>
                  <div className="bootcamps-stat-label">Bootcamps</div>
                </div>
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">10+</div>
                  <div className="bootcamps-stat-label">Partners</div>
                </div>
              </div>
            </div>

            <div className="bootcamps-hero-visual">
              <div className="bootcamps-feature-card">
                <div className="bootcamps-feature-header">
                  <span className="bootcamps-feature-badge">Program Highlights</span>
                </div>
                <ul className="bootcamps-feature-list">
                  <li>
                    <CheckCircle className="bootcamps-check-icon" />
                    <span>Beginner to advanced learning paths</span>
                  </li>
                  <li>
                    <CheckCircle className="bootcamps-check-icon" />
                    <span>Hands-on coding with industry frameworks</span>
                  </li>
                  <li>
                    <CheckCircle className="bootcamps-check-icon" />
                    <span>Expert-led sessions &amp; mentorship</span>
                  </li>
                  <li>
                    <CheckCircle className="bootcamps-check-icon" />
                    <span>Real-world quantum projects</span>
                  </li>
                  <li>
                    <CheckCircle className="bootcamps-check-icon" />
                    <span>Completion certificates &amp; alumni network</span>
                  </li>
                </ul>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bootcamps-how-it-works-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">How Our Bootcamps Work</h2>
            <p className="bootcamps-section-description">A structured approach to quantum mastery</p>
          </div>

          <div className="bootcamps-steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="bootcamps-step-card">
                <span className="bootcamps-step-bg-num">{step.step}</span>
                <div className={`bootcamps-step-icon-wrap bootcamps-step-${step.color}`}>
                  <step.icon className="bootcamps-step-icon" />
                </div>
                <h3 className="bootcamps-step-title">{step.title}</h3>
                <p className="bootcamps-step-description">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Bootcamps */}
      <section className="bootcamps-past-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">Past Bootcamps</h2>
            <p className="bootcamps-section-description">A look at our recent programs and their impact</p>
          </div>

          {/* Slider — mobile only */}
          <div className="bootcamps-slider-wrapper">
            <button className="bootcamps-slider-arrow left" onClick={handlePrev}>
              <ChevronLeft />
            </button>
            <div className="bootcamps-slider">
              {pastBootcamps.map((bootcamp, index) => (
                <div
                  key={index}
                  className={`bootcamps-slider-item ${index === currentCard ? 'active' : 'inactive'}`}
                >
                  <BootcampCard bootcamp={bootcamp} />
                </div>
              ))}
            </div>
            <button className="bootcamps-slider-arrow right" onClick={handleNext}>
              <ChevronRight />
            </button>
          </div>

          {/* Grid — tablet and up */}
          <div className="bootcamps-past-grid">
            {pastBootcamps.map((bootcamp, index) => (
              <BootcampCard key={index} bootcamp={bootcamp} />
            ))}
          </div>
        </div>
      </section>

      {/* Quantum Frameworks — 2×3 Grid */}
      <section className="bootcamps-frameworks-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">Quantum Frameworks We Master</h2>
            <p className="bootcamps-section-description">
              Learn from industry-leading quantum computing frameworks and tools used by top tech companies worldwide.
            </p>
          </div>

          <div className="bootcamps-frameworks-grid">
            {frameworks.map((framework, index) => (
              <div key={index} className="bootcamps-framework-card">
                <img
                  src={framework.logo}
                  alt={framework.name}
                  className="bootcamps-framework-logo"
                />
                <span className="bootcamps-framework-name">{framework.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
};

export default BootcampsPage;
